import { config } from "./screen-canvas-config";
import { useEffect, useState, useRef } from "react";

//@ts-ignore
const ScreenCanvas = ({
    // fontSize = 46, 
    //@ts-expect-error - gen
    siteData,
    //@ts-expect-error - gen
    domain,
    //@ts-expect-error - gen
    extraVerticalPadding,
}) => {
    const [image, setImage] = useState(null);
    const [elements, setElements] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [scaleFactor, setScaleFactor] = useState(1);
    const [dynamicHeight, setDynamicHeight] = useState(4000); // Default fallback height
    const [isMobileView, setIsMobileView] = useState(
        typeof window !== 'undefined' ? window.innerWidth < 768 : false
    );
    const [openTooltip, setOpenTooltip] = useState(null);


    const [fontSize, setFontSize] = useState(26); // Default to 26 (for 1920px+)
    const {
        slots,
        lineWidth,
        screenshotWidth,
        screenshotHeight,
        canvasHeight,
        canvasWidth,
        padding,
        industrySector
    } = config;

    const tooltipRefs = useRef([]);
    const [tooltipWidths, setTooltipWidths] = useState({});
    const containerRef = useRef(null);
    const effectivePadding = isMobileView ? 0 : padding;


    useEffect(() => {
        const handleResize = () => {
            const windowWidth = window.innerWidth;
            setIsMobileView(windowWidth < 768); // Set mobile view flag
            //@ts-expect-error - gen
            const containerWidth = containerRef.current?.offsetWidth || windowWidth;
            const newDynamicHeight = Math.max(containerWidth * 0.4, 600);
            setDynamicHeight(newDynamicHeight);

            // Font size calculation (keep this but adjust for mobile)
            const maxFontSize = isMobileView ? 20 : 50;
            const minFontSize = isMobileView ? 18 : 24;
            const progress = Math.min(1, Math.max(0, (1920 - windowWidth) / (1920 - 400)));
            const newFontSize = minFontSize + (maxFontSize - minFontSize) * progress;
            setFontSize(newFontSize);

            // Only apply scaling for desktop (mobile will be actual size)
            const newScale = isMobileView ? 1 : Math.min(1, windowWidth / 1920) * 0.93;
            setScaleFactor(newScale);
        };

        handleResize();
        // Debounced resize handler
        let animationFrame: number;
        const optimizedResize = () => {
            if (animationFrame) cancelAnimationFrame(animationFrame);
            animationFrame = requestAnimationFrame(handleResize);
        };

        window.addEventListener('resize', optimizedResize);
        return () => {
            window.removeEventListener('resize', optimizedResize);
            if (animationFrame) cancelAnimationFrame(animationFrame);
        };

    }, [isMobileView]);

    // 2. Update the image and elements loading:
    useEffect(() => {
        if (siteData) {
            setLoading(true);
            try {
                const img = new Image();
                // Always use mobile data if in mobile view, regardless of initial load
                const sourceData = isMobileView ?
                    (siteData.mobile || siteData.desktop) :
                    siteData.desktop;

                img.onload = () => {
                    //@ts-expect-error - gen
                    setImage(img);
                    setElements(sourceData.elements || []);
                    setLoading(false);
                };
                img.onerror = () => {
                    throw new Error("Failed to load image from data");
                };
                img.src = sourceData.screenshot;
            } catch (err) {
                setError(
                    //@ts-expect-error - gen
                    err.message);
                setLoading(false);
            }
        }
    }, [siteData, isMobileView]);

    useEffect(() => {
        if (tooltipRefs.current.length > 0) {
            const newWidths = {};
            tooltipRefs.current.forEach((ref, index) => {
                if (ref) {
                    //@ts-ignore
                    newWidths[index] = ref.offsetWidth;
                }
            });
            setTooltipWidths(newWidths);
        }
    }, [elements]);

    if (loading) return <div></div>;
    if (error) return <div>Error: {error}</div>;
    if (!image) return <div>No image data available</div>;

    //@ts-ignore
    const scaleX = screenshotWidth / image.width;
    //@ts-ignore
    const scaleY = screenshotHeight / image.height;
    const usedSlots = new Set();

    // Function to check if two lines intersect
    //@ts-ignore
    const doLinesIntersect = (line1, line2) => {
        const [x1, y1, x2, y2] = line1;
        const [x3, y3, x4, y4] = line2;

        const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
        if (denominator === 0) return false;

        const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
        const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

        const epsilon = 0.0001;
        return (
            ua >= -epsilon && ua <= 1 + epsilon && ub >= -epsilon && ub <= 1 + epsilon
        );
    };

    //@ts-ignore
    const calculateLine = (slot, boundingArea) => {
        const tooltipCenterX = effectivePadding + slot.x + slot.width / 2;
        const tooltipCenterY = effectivePadding + slot.y + slot.height / 2;
        const centerX = (boundingArea[0] + boundingArea[2]) / 2;
        const centerY = (boundingArea[1] + boundingArea[3]) / 2;

        return [tooltipCenterX, tooltipCenterY, centerX, centerY];
    };

    // Function to check if two bounding areas overlap
    //@ts-ignore
    const doBoundingAreasOverlap = (area1, area2) => {
        const [x1, y1, x2, y2] = area1;
        const [x3, y3, x4, y4] = area2;
        return !(x2 <= x3 || x1 >= x4 || y2 <= y3 || y1 >= y4);
    };

    // Function to check for keyword matches
    //@ts-ignore
    const checkForKeywordMatches = (text, keywords) => {
        if (!text || !keywords) return [];
        //@ts-ignore
        return keywords.filter((keyword) =>
            text.toLowerCase().includes(keyword.toLowerCase())
        );
    };

    const processElements = () => {
        //@ts-ignore
        const processedElements = [];
        //@ts-ignore
        const imgElements = [];

        const mobileSlots = [
            { y: 2005, x: 1150, width: 100, height: 100 },
            { y: 2400, x: 10, width: 100, height: 100 },
            { y: 3210, x: 20, width: 100, height: 100 },
            { y: 4190, x: 1120, width: 100, height: 100 }
        ];

        const availableSlots = [
            ...slots,
            ...(isMobileView ? mobileSlots : [])
        ];
        const sortedElements = elements
            .map((element, index) => ({ element, index }))
            .sort((a, b) => {
                //@ts-ignore
                const aY = a.element.boundingArea[1];
                //@ts-ignore
                const bY = b.element.boundingArea[1];
                return aY - bY;
            });

        sortedElements.forEach(({ element, index }) => {
            //@ts-ignore
            const boundingArea = element.boundingArea || [0, 0, 0, 0];

            if (
                boundingArea.length !== 4 ||
                //@ts-ignore
                boundingArea.every((val) => val === 0) ||
                boundingArea[0] <= 0 ||
                boundingArea[1] <= 0 ||
                boundingArea[0] >= screenshotWidth + effectivePadding ||
                boundingArea[2] >= screenshotWidth + effectivePadding ||
                boundingArea[1] >= canvasHeight - effectivePadding ||
                boundingArea[3] >= screenshotHeight
            ) {
                return;
            }

            const minVisibleSize = 50; // Minimum rendered size (after scaling)
            const isSmallImage =
                (boundingArea[2] - boundingArea[0]) * scaleX < minVisibleSize ||
                (boundingArea[3] - boundingArea[1]) * scaleY < minVisibleSize;
            const hasExcludedKeywords = [
                "facebook",
                "google",
                "instagram",
                "twitter",
                "linkedin",
                "credit",
                "mastercard",
                "visa",
            ].some(
                (keyword) =>
                    //@ts-ignore
                    (element.filename &&
                        //@ts-ignore
                        element.filename.toLowerCase().includes(keyword)) ||
                    //@ts-ignore
                    (element.alt && element.alt.toLowerCase().includes(keyword))
            );

            if (isSmallImage || hasExcludedKeywords) {
                return;
            }
            // console.log({
            //     originalWidth: boundingArea[2] - boundingArea[0],
            //     originalHeight: boundingArea[3] - boundingArea[1],
            //     scaleX,
            //     scaleY,
            //     renderedWidth: (boundingArea[2] - boundingArea[0]) * scaleX,
            //     renderedHeight: (boundingArea[3] - boundingArea[1]) * scaleY,
            //     isSmallImage,
            // });
            const transformedBoundingArea = [
                effectivePadding + boundingArea[0] * (isMobileView ? 1 : scaleX), // Adjust scaling for mobile
                effectivePadding + boundingArea[1] * (isMobileView ? 1 : scaleY),
                effectivePadding + boundingArea[2] * (isMobileView ? 1 : scaleX),
                effectivePadding + boundingArea[3] * (isMobileView ? 1 : scaleY),
            ];


            //@ts-ignore
            if (element.type === "img") {
                //@ts-ignore
                const isOverlapping = imgElements.some((img) =>
                    doBoundingAreasOverlap(transformedBoundingArea, img.boundingArea)
                );

                if (isOverlapping) {
                    return;
                }

                imgElements.push({ element, boundingArea: transformedBoundingArea });
            }

            const centerX =
                (transformedBoundingArea[0] + transformedBoundingArea[2]) / 2;
            const centerY =
                (transformedBoundingArea[1] + transformedBoundingArea[3]) / 2;

            let closestSlot = null;
            let minDistance = Infinity;

            availableSlots.forEach((slot) => {
                const slotCenterX = slot.x + slot.width / 2;
                const slotCenterY = slot.y + slot.height / 2;
                const distance = Math.sqrt(
                    (slotCenterX - centerX) ** 2 + (slotCenterY - centerY) ** 2
                );

                if (distance < minDistance) {
                    minDistance = distance;
                    closestSlot = slot;
                }
            });

            if (!closestSlot) {
                return;
            }

            const slotIndex = availableSlots.indexOf(closestSlot);
            if (slotIndex !== -1) {
                availableSlots.splice(slotIndex, 1);
            }

            //@ts-ignore
            if (closestSlot.x === 10 && closestSlot.y === 400) {
                const tooltipBoundingArea = [
                    //@ts-ignore
                    closestSlot.x,
                    //@ts-ignore
                    closestSlot.y,
                    //@ts-ignore
                    closestSlot.x + closestSlot.width,
                    //@ts-ignore
                    closestSlot.y + closestSlot.height,
                ];
                //@ts-ignore
                const overlaps = elements.some((e) =>
                    //@ts-ignore
                    doBoundingAreasOverlap(tooltipBoundingArea, e.boundingArea)
                );

                if (overlaps) {
                    //@ts-ignore
                    closestSlot.width = 370;
                }
            }

            let description = "";
            let isEligibleForSlot = true;
            const newKeywords = ["move", "moving"];
            const keywordsToCheck = siteData?.keywords
                ? [...siteData.keywords, ...newKeywords]
                : newKeywords;

            let h1Seen = false;
            // @ts-ignore
            if (element.type === "placeholder") {
                // @ts-ignore
                description = element.text;
                // @ts-ignore
            } else if (["h1", "h2", "h3"].includes(element.type)) {
                // @ts-ignore
                if (element.type === "h1") {
                    if (h1Seen) return; // skip if an h1 has already been processed
                    h1Seen = true;
                }

                // @ts-ignore
                const matchedKeywords = element.text
                    // @ts-ignore
                    ? checkForKeywordMatches(element.text, keywordsToCheck)
                    : [];

                if (matchedKeywords.length > 0) {
                    description = `The heading contains the keyword${matchedKeywords.length > 1 ? "s" : ""} "${matchedKeywords.join('", "')}".`;
                    isEligibleForSlot = false;
                } else {
                    description = "The heading does not contain any keywords.";
                    isEligibleForSlot = true;
                }
            }
            //         const matchedKeywords = element.text
            //                             // @ts-ignore

            //         ? checkForKeywordMatches(element.text, keywordsToCheck)
            //         : [];

            //     if (matchedKeywords.length > 0) {
            //                             // @ts-ignore

            //         description = `The ${element.type} "${element.text}" contains the keyword${matchedKeywords.length > 1 ? "s" : ""} "${matchedKeywords.join('", "')}".`;
            //         isEligibleForSlot = false;
            //     } else {
            //                             // @ts-ignore

            //         description = `The ${element.type} "${element.text}" does not contain any keywords.`;
            //         isEligibleForSlot = true;
            //     }
            // }
            //@ts-ignore
            else if (element.type === "img") {
                //@ts-ignore
                const imageMatchedKeywords = element.filename
                    //@ts-ignore
                    ? checkForKeywordMatches(element.filename, keywordsToCheck)
                    : [];
                //@ts-ignore
                const altMatchedKeywords = element.alt
                    //@ts-ignore
                    ? checkForKeywordMatches(element.alt, keywordsToCheck)
                    : [];

                if (imageMatchedKeywords.length > 0 && altMatchedKeywords.length > 0) {
                    isEligibleForSlot = false;
                    description = "";
                } else if (imageMatchedKeywords.length > 0) {
                    isEligibleForSlot = true;
                    description = "The alternative text does not contain any keywords.";
                } else if (altMatchedKeywords.length > 0) {
                    isEligibleForSlot = true;
                    description = "The image filename does not contain any keywords.";
                } else {
                    isEligibleForSlot = true;
                    description = "Both the image filename and alternative text contain no keywords.";
                }
            }
             

            if (!isEligibleForSlot) {
                return;
            }

            const line = calculateLine(closestSlot, transformedBoundingArea);

            processedElements.push({
                element,
                assignedSlot: closestSlot,
                line,
                boundingArea: transformedBoundingArea,
                description,
                isVisible: true
            });
        });

        for (let i = 0; i < processedElements.length; i++) {
            for (let j = i + 1; j < processedElements.length; j++) {
                //@ts-ignore
                const line1 = processedElements[i].line;
                //@ts-ignore
                const line2 = processedElements[j].line;

                if (doLinesIntersect(line1, line2)) {
                    //@ts-ignore
                    const temp = processedElements[i].assignedSlot;
                    //@ts-ignore
                    processedElements[i].assignedSlot = processedElements[j].assignedSlot;
                    //@ts-ignore
                    processedElements[j].assignedSlot = temp;

                    //@ts-ignore
                    processedElements[i].line = calculateLine(
                        //@ts-ignore
                        processedElements[i].assignedSlot,
                        //@ts-ignore
                        processedElements[i].boundingArea
                    );
                    //@ts-ignore
                    processedElements[j].line = calculateLine(
                        //@ts-ignore
                        processedElements[j].assignedSlot,
                        //@ts-ignore
                        processedElements[j].boundingArea
                    );
                }
            }
        }

        if (availableSlots.length > 0) {
            //@ts-ignore
            const hasH1 = elements.some(
                (el) =>
                    //@ts-ignore
                    el.type === "h1" &&
                    //@ts-ignore
                    el.text !== false &&
                    //@ts-ignore
                    el.boundingArea &&
                    //@ts-ignore
                    el.boundingArea.length === 4 &&
                    //@ts-ignore
                    !el.boundingArea.every((val) => val === 0)
            );

            if (!hasH1 && availableSlots.length > 0) {
                const lastSlot = availableSlots[availableSlots.length - 1];
                processedElements.push({
                    type: "placeholder",
                    text: "No main heading (H1) in the intro screen.",
                    assignedSlot: lastSlot,
                    boundingArea: [0, 0, 0, 0],
                    description: "No main heading (H1) in the intro screen.",
                });
                availableSlots.pop();
            }

            const sitemapPass = !!siteData?.sitemap;
            const robotsPass = !!siteData?.robotsTxt?.exists;

            if (!sitemapPass && availableSlots.length > 0) {
                const lastSlot = availableSlots[availableSlots.length - 1];
                processedElements.push({
                    type: "placeholder",
                    text: "There is no Sitemap.",
                    assignedSlot: lastSlot,
                    boundingArea: [0, 0, 0, 0],
                    description: "There is no Sitemap.",
                });
                availableSlots.pop();
            }

            if (!robotsPass && availableSlots.length > 0) {
                const lastSlot = availableSlots[availableSlots.length - 1];
                processedElements.push({
                    type: "placeholder",
                    text: "There is no Robots.txt.",
                    assignedSlot: lastSlot,
                    boundingArea: [0, 0, 0, 0],
                    description: "There is no Robots.txt.",
                });
                availableSlots.pop();
            }

            // PageSpeed checks
            //@ts-ignore
            const parseSpeedValue = (value) => {
                if (!value) return 0;
                return parseFloat(value.replace(/[^0-9.]/g, "")); // Remove non-numeric characters and parse
            };

            const speedIndexPass = parseSpeedValue(siteData?.pagespeed?.speedIndex) <= 2;
            const fcpPass = parseSpeedValue(siteData?.pagespeed?.firstContentfulPaint) <= 2;
            const interactivePass = parseSpeedValue(siteData?.pagespeed?.interactive) <= 3;
            const responsiveImagesPass = !siteData?.pagespeed?.usesResponsiveImages?.includes("Potential savings");
            const unminJSPass = !siteData?.pagespeed?.unminifiedJavaScript?.includes("Potential savings");
            const unminCSSPass = !siteData?.pagespeed?.unminifiedCss?.includes("Potential savings");
            const renderBlockingPass = !siteData?.pagespeed?.renderBlockingResources?.includes("Potential savings");
            const byteWeightPass = parseSpeedValue(siteData?.pagespeed?.totalByteWeight) <= 2000;
            const networkRequestsPass = siteData?.pagespeed?.networkRequests === "N/A";
            const redirectsPass = siteData?.pagespeed?.redirects === "N/A";
            const readabilityPass = siteData?.readability?.score > 70;
            const contentLengthPass = siteData?.contentLength?.status === true;
            const headingHierarchyPass = !!siteData?.headingHierarchy?.status;
            const annotatedWebpPass = !!siteData?.annotatedWebp;

            const hasPageSpeedFailure = !speedIndexPass || !fcpPass || !interactivePass;

            if (hasPageSpeedFailure && availableSlots.length > 0) {
                const lastSlot = availableSlots[availableSlots.length - 1];
                processedElements.push({
                    type: "placeholder",
                    text: "Page Speed issues detected.",
                    assignedSlot: lastSlot,
                    boundingArea: [0, 0, 0, 0], // Dummy bounding area
                    description: "Page speed issues detected.",
                });
                availableSlots.pop();
            }

            // Responsive Images Check
      if (!responsiveImagesPass && availableSlots.length > 0) {
        const lastSlot = availableSlots[availableSlots.length - 1];
        processedElements.push({
          type: "placeholder",
          text: "The website uses images that are not responsive, which can negatively impact performance on different devices.",
          assignedSlot: lastSlot,
          boundingArea: [0, 0, 0, 0], // Dummy bounding area
          description: "The website uses images that are not responsive, which can negatively impact performance on different devices.",
        });
        availableSlots.pop();
      }

      // Unminified JavaScript Check
      if (!unminJSPass && availableSlots.length > 0) {
        const lastSlot = availableSlots[availableSlots.length - 1];
        processedElements.push({
          type: "placeholder",
          text: "The website contains JavaScript code that has not been compressed, which can increase load times.",
          assignedSlot: lastSlot,
          boundingArea: [0, 0, 0, 0], // Dummy bounding area
          description: "The website contains unminified JavaScript, which can increase load times.",
        });
        availableSlots.pop();
      }

      // Unminified CSS Check
      if (!unminCSSPass && availableSlots.length > 0) {
        const lastSlot = availableSlots[availableSlots.length - 1];
        processedElements.push({
          type: "placeholder",
          text: "The website contains unminified CSS, which can increase load times.",
          assignedSlot: lastSlot,
          boundingArea: [0, 0, 0, 0], // Dummy bounding area
          description: "The website contains unminified CSS, which can increase load times.",
        });
        availableSlots.pop();
      }

      // Render Blocking Resources Check
      if (!renderBlockingPass && availableSlots.length > 0) {
        const lastSlot = availableSlots[availableSlots.length - 1];
        processedElements.push({
          type: "placeholder",
          text: "The website has a lot of code that is not necessary for the most important content, so it makes the page slow.",
          assignedSlot: lastSlot,
          boundingArea: [0, 0, 0, 0], // Dummy bounding area
          description: "The website has render-blocking resources, which can delay page rendering.",
        });
        availableSlots.pop();
      }

      // Byte Weight Check
      if (!byteWeightPass && availableSlots.length > 0) {
        const lastSlot = availableSlots[availableSlots.length - 1];
        processedElements.push({
          type: "placeholder",
          text: "The website has a very high total file size, which slows down the loading of the page.",
          assignedSlot: lastSlot,
          boundingArea: [0, 0, 0, 0], // Dummy bounding area
          description: "The website has a high total byte weight, which can slow down page load times.",
        });
        availableSlots.pop();
      }

      // Network Requests Check
      if (!networkRequestsPass && availableSlots.length > 0) {
        const lastSlot = availableSlots[availableSlots.length - 1];
        processedElements.push({
          type: "placeholder",
          text: "The website downloads too many individual items, which slows the page loading.",
          assignedSlot: lastSlot,
          boundingArea: [0, 0, 0, 0], // Dummy bounding area
          description: "The website downloads too many individual items, which slows the page loading.",
        });
        availableSlots.pop();
      }

      // Redirects Check
      if (!redirectsPass && availableSlots.length > 0) {
        const lastSlot = availableSlots[availableSlots.length - 1];
        processedElements.push({
          type: "placeholder",
          text: "The website has unnecessary redirects, which can slow down page load times.",
          assignedSlot: lastSlot,
          boundingArea: [0, 0, 0, 0], // Dummy bounding area
          description: "The website has unnecessary redirects, which can slow down page load times.",
        });
        availableSlots.pop();
      }

            // // Readability Check
            // if (!readabilityPass && availableSlots.length > 0) {
            //     const lastSlot = availableSlots[availableSlots.length - 1];
            //     processedElements.push({
            //         type: "placeholder",
            //         text: "The website's readability score is below 70, which may affect user engagement.",
            //         assignedSlot: lastSlot,
            //         boundingArea: [0, 0, 0, 0], // Dummy bounding area
            //         description: "The website's readability score is below 70, which may affect user engagement.",
            //     });
            //     availableSlots.pop();
            // }

            // Content Length Check
            if (!contentLengthPass && availableSlots.length > 0) {
                const lastSlot = availableSlots[availableSlots.length - 1];
                processedElements.push({
                    type: "placeholder",
                    text: "The website's content length is insufficient, which may impact SEO and user engagement.",
                    assignedSlot: lastSlot,
                    boundingArea: [0, 0, 0, 0], // Dummy bounding area
                    description: "The website's content length is insufficient, which may impact SEO and user engagement.",
                });
                availableSlots.pop();
            }

            // Heading Hierarchy Check
            if (!headingHierarchyPass && availableSlots.length > 0) {
                const lastSlot = availableSlots[availableSlots.length - 1];
                processedElements.push({
                    type: "placeholder",
                    text: "The website's heading hierarchy is not properly structured, which can affect SEO and accessibility.",
                    assignedSlot: lastSlot,
                    boundingArea: [0, 0, 0, 0], // Dummy bounding area
                    description: "The website's heading hierarchy is not properly structured, which can affect SEO and accessibility.",
                });
                availableSlots.pop();
            }
            if (isMobileView) {
                // Separate visible and placeholder elements
                //@ts-expect-error
                const visibleElements = processedElements.filter(el => el.isVisible);
                //@ts-expect-error
                const placeholderElements = processedElements.filter(el => !el.isVisible);

                // Calculate positions for placeholders (centered horizontally, stacked vertically)
                const placeholderStartY = 100; // Start below the main content
                const placeholderSpacing = 60; // Vertical spacing between placeholders

                placeholderElements.forEach((el, index) => {
                    // Assign a virtual position centered in the container
                    el.boundingArea = [
                        375 / 2 - 20, // Left (center - half width)
                        placeholderStartY + (index * placeholderSpacing), // Top
                        375 / 2 + 20, // Right (center + half width)
                        placeholderStartY + (index * placeholderSpacing) + 40 // Bottom
                    ];

                    // Update the transformed bounding area for rendering

                    //@ts-expect-error - gen
                    el.transformedBoundingArea = [
                        el.boundingArea[0],
                        el.boundingArea[1],
                        el.boundingArea[2],
                        el.boundingArea[3]
                    ];
                });

                return [...visibleElements, ...placeholderElements];
            }
        }

        return processedElements;
    };

    const processedElements = processElements();
    // Count the number of red (element issues) and orange (performance issues) circles
    const redCirclesCount = processedElements.filter(element => {
        //@ts-expect-error - gen
        const el = element.element;
        return el && ["h1", "h2", "h3", "img"].includes(el.type?.toLowerCase());
    }).length;

    const orangeCirclesCount = processedElements.filter(element => {
        // Performance issues are either:
        // 1. Placeholder elements (like "Page Speed issues detected")
        // 2. Elements that aren't h1/h2/h3/img
        return element.type === "placeholder" ||
            //@ts-expect-error - gen
            (element.element && !["h1", "h2", "h3", "img"].includes(element.element.type?.toLowerCase()));
    }).length;

    return (
        <>

            {isMobileView && (
                <div className="container" style={{ maxWidth: 375, display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                    {redCirclesCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                marginTop: 3,
                                backgroundColor: '#ff4a4a',
                                borderRadius: '50%',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                flexShrink: 0,
                            }}></div>
                            <p className="mt-0 pt-0" style={{ margin: 0,
                                lineHeight:1.3 }}>
                                Click on the red buttons below to see page elements that are missing keywords.
                            </p>
                        </div>
                    )}
                    {orangeCirclesCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'start', gap: '8px' }}>
                            <div style={{
                                width: '16px',
                                height: '16px',
                                marginTop: 3,
                                backgroundColor: '#ffa500',
                                borderRadius: '50%',
                                border: '2px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                flexShrink: 0, 
                            }}></div>
                            <p className="mt-0 pt-0" style={{ margin: 0,
                                lineHeight:1.3 }}>
                                The orange buttons refer to general performance issues like page speed.
                            </p>
                        </div>
                    )}
                </div>
            )}
            <div
                ref={containerRef}
                style={{
                    position: "relative",
                    width: isMobileView ? "375px" : "100%",
                    margin: "0 auto",
                    display: "flex",
                    justifyContent: "center",
                }}
                className="tooltips"
            >
                <div
                    style={{
                        position: "relative",
                        borderRadius: "16px",
                        // width: `${canvasWidth + extraVerticalPadding}px`,
                        width: "100%",
                        height: isMobileView ? "812px" : "72vw", // Fixed height for mobile


                    }}
                >
                    {!isMobileView && (
                        <div style={{
                            transform: `scale(${scaleFactor})`,
                            transformOrigin: "0 0"
                        }}>
                            <div
                                style={{
                                    position: "absolute",
                                    top: effectivePadding,
                                    left: effectivePadding,
                                    width: `${screenshotWidth - 20}px`,
                                    height: `${canvasHeight + extraVerticalPadding}px`,
                                    borderRadius: "16px",
                                    overflow: "hidden",
                                }}
                            >
                                <img
                                    src={siteData.desktop.screenshot}
                                    alt="Website Screenshot"
                                    style={{
                                        width: `${screenshotWidth}px`,
                                        height: `${screenshotHeight}px`,
                                        objectFit: "cover",
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {isMobileView && (
                        <div
                            style={{
                                position: "relative",
                                width: "375px",
                                height: "812px",
                                margin: "0 auto", // Center the container
                                borderRadius: "16px",
                                overflow: "hidden",
                            }}
                        >
                            <img
                                src={siteData.mobile.screenshot}
                                alt="Mobile Website Screenshot"
                                style={{
                                    width: "375px",
                                    height: "812px",
                                    objectFit: "cover",
                                }}
                            />
                        </div>
                    )}
                </div>
             {isMobileView ? (
    <div style={{
        position: "absolute",
        width: "100%",
        height: "100%",
        top: 0,
        left: 0,
        pointerEvents: "none",
    }}>
        {/* First render red circles (element issues) in their original positions */}
        {processedElements
            .filter(element => {
                                //@ts-expect-error -gen
                const el = element.element;
                return el && ["h1", "h2", "h3", "img"].includes(el.type?.toLowerCase());
            })
            .map((element, index) => {
                const { boundingArea, description } = element;
                const centerX = (boundingArea[0] + boundingArea[2]) / 2;
                const centerY = (boundingArea[1] + boundingArea[3]) / 2;

                return (
                    <div style={{ position: "relative" }} key={`mobile-red-${index}`}>
                        {/* Red Circle */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                //@ts-expect-error -gen
                                setOpenTooltip(`red-${index}`);
                            }}
                            style={{
                                position: "absolute",
                                top: `${centerY}px`,
                                left: `${centerX}px`,
                                transform: "translate(-50%, -50%)",
                                width: "30px",
                                height: "30px",
                                backgroundColor: "#ff4a4a",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "20px",
                                cursor: "pointer",
                                border: "2px solid white",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                zIndex: 15,
                                pointerEvents: "auto",
                            }}
                        >
                            !
                        </div>

                        {/* Tooltip */}
                        {openTooltip === `red-${index}` && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: `${centerY + 20}px`,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "calc(100% - 40px)",
                                    maxWidth: "350px",
                                    backgroundColor: "white",
                                    border: "2px solid #ff4a4a",
                                    color: "#333",
                                    borderRadius: "12px",
                                    padding: "15px",
                                    fontSize: "16px",
                                    lineHeight: 1.4,
                                    zIndex: 20,
                                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                                    pointerEvents: "auto",
                                }}
                            >
                                {description}
                            </div>
                        )}
                    </div>
                );
            })}

        {/* Then render orange circles (performance issues) with vertical spacing */}
        {(() => {
            const orangeElements = processedElements.filter(element => {
                                //@ts-expect-error -gen
                const el = element.element;
                return !el || !["h1", "h2", "h3", "img"].includes(el.type?.toLowerCase());
            });

            // Calculate positions for orange circles with vertical spacing
            const positionedOrangeElements = [];
            const VERTICAL_SPACING = 80; // Minimum vertical distance between orange circles
            const HORIZONTAL_CENTER = 375 / 2; // Center of mobile view
            
            // First get all red circle positions
            const redCirclePositions = processedElements
                                //@ts-expect-error -gen
                .filter(el => el.element && ["h1", "h2", "h3", "img"].includes(el.element.type?.toLowerCase()))
                .map(el => ({
                    y: (el.boundingArea[1] + el.boundingArea[3]) / 2,
                    height: el.boundingArea[3] - el.boundingArea[1]
                }));

            // Sort orange elements by their original vertical position
            const sortedOrangeElements = [...orangeElements].sort((a, b) => 
                (a.boundingArea[1] + a.boundingArea[3]) / 2 - (b.boundingArea[1] + b.boundingArea[3]) / 2
            );

            let lastOrangeY = 0;
            
            return sortedOrangeElements.map((element, index) => {
                const { description } = element;
                let centerY = (element.boundingArea[1] + element.boundingArea[3]) / 2;
                
                // If this isn't the first orange circle, enforce vertical spacing
                if (index > 0) {
                    const minY = lastOrangeY + VERTICAL_SPACING;
                    if (centerY < minY) {
                        centerY = minY;
                    }
                }
                
                // Check for collisions with red circles
                for (const red of redCirclePositions) {
                    if (Math.abs(centerY - red.y) < VERTICAL_SPACING) {
                        centerY = red.y + red.height/2 + VERTICAL_SPACING;
                    }
                }
                
                lastOrangeY = centerY;
                
                return (
                    <div style={{ position: "relative" }} key={`mobile-orange-${index}`}>
                        {/* Orange Circle */}
                        <div
                            onClick={(e) => {
                                e.stopPropagation();
                                //@ts-expect-error -gen
                                setOpenTooltip(`orange-${index}`);
                            }}
                            style={{
                                position: "absolute",
                                top: `${centerY}px`,
                                left: `${HORIZONTAL_CENTER}px`,
                                transform: "translate(-50%, -50%)",
                                width: "30px",
                                height: "30px",
                                backgroundColor: "#ffa500",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontWeight: "bold",
                                fontSize: "20px",
                                cursor: "pointer",
                                border: "2px solid white",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
                                zIndex: 15,
                                pointerEvents: "auto",
                            }}
                        >
                            !
                        </div>

                        {/* Tooltip */}
                        {openTooltip === `orange-${index}` && (
                            <div
                                style={{
                                    position: "absolute",
                                    top: `${centerY + 20}px`,
                                    left: "50%",
                                    transform: "translateX(-50%)",
                                    width: "calc(100% - 40px)",
                                    maxWidth: "350px",
                                    backgroundColor: "white",
                                    border: "2px solid #ffa500",
                                    color: "#333",
                                    borderRadius: "12px",
                                    padding: "15px",
                                    fontSize: "16px",
                                    lineHeight: 1.4,
                                    zIndex: 20,
                                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
                                    pointerEvents: "auto",
                                }}
                            >
                                {description}
                            </div>
                        )}
                    </div>
                );
            });
        })()}
    </div>
)   : (<div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        transform: `scale(${scaleFactor})`,
                        transformOrigin: "0 0",
                    }}
                >
                    {processedElements.map((element, index) => {
                        const { assignedSlot, boundingArea, description, type } = element;
                        if (!assignedSlot) {
                            return null;
                        }

                        //@ts-ignore
                        const tooltipWidth = tooltipWidths[index] || 300;
                        const centerX = (boundingArea[0] + boundingArea[2]) / 2;
                        const centerY = (boundingArea[1] + boundingArea[3]) / 2;
                        const lineStartX = assignedSlot.x + tooltipWidth / 2;
                        const lineStartY = assignedSlot.y;

                        const widthHere =
                            assignedSlot.y === 400 && assignedSlot.x === 10 ? 370 : 'auto';

                        return (
                            <div key={index}>
                                {/* @ts-ignore */}

                                {!(boundingArea[0] === 0 && boundingArea[1] === 0 && boundingArea[2] === 0 && boundingArea[3] === 0) && (
                                    <div
                                        //@ts-expect-error - gen
                                        className={index}
                                        style={{
                                            position: "absolute",
                                            top: lineStartY,
                                            left: lineStartX,
                                            width: Math.sqrt(
                                                (centerX - lineStartX) ** 2 + (centerY - lineStartY) ** 2
                                            ),
                                            height: lineWidth,
                                            backgroundColor: "black",
                                            transform: `rotate(${Math.atan2(
                                                centerY - lineStartY,
                                                centerX - lineStartX
                                            )}rad)`,
                                            transformOrigin: "0 0",
                                        }}
                                    />

                                )}
                                <div
                                    //@ts-expect-error - gen

                                    ref={(el) => (tooltipRefs.current[index] = el)}
                                    style={{
                                        position: "absolute",
                                        letterSpacing: '-1px',
                                        top: assignedSlot.y,
                                        left: assignedSlot.x,
                                        //      width: widthHere,//
                                        // @ts-expect-error -gen
    width: assignedSlot.maxWidth ? assignedSlot.maxWidth : 800, // Use slot maxWidth if set                                        minWidth: "300px",
                                        maxWidth: "1000px",
                                        backgroundColor: "white",
                                        border: "2px solid black",
                                        color: "#333",
                                        borderRadius: "40px",
                                        padding: "15px 15px 20px 25px",
                                        fontSize: `${fontSize}px`,
                                        lineHeight: 1.2,
                                        pointerEvents: "none",
                                        zIndex: 10,
                                    }}
                                    className="d-none d-md-block md-d-block"
                                >
                                    {description}
                                </div>



                            </div>
                        );
                    })}
                </div>

                )
                }
            </div></>);
};

export default ScreenCanvas;